import { decimalSeparatedPrice, hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps, lensConfiguratorHeaderTitleMap } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import CloseIcon from "@/assets/icons/close.svg?react";
import noImage from "@/assets/images/no_image.svg";
import { ChoiceType, TranslationData } from "@/common/lib/types";
import LazyImage from "@/components/lazyImage/LazyImage";
import RenderPrice from "@/components/productPrice/RenderPrice";
import { cn } from "@/lib/tailwind";
import { getLangFromLocale } from "@/utils/common";
import { getPreviousStep, translate } from "@/utils/lensConfigurator";
import { useMemo, useState } from "react";
import { useConfiguratorContext } from "../../context/Provider";
import ConfirmationModal from "../ConfirmationModal";
import Navigation from "../Navigation";
import SummaryView from "./SummaryView";
import TotalSection from "./TotalSection";

interface ILensSummaryProps {
  locale: string;
  currencyCode: string;
  isPurchased?: boolean;
  translations: TranslationData;
  onCloseLensConfigurator: () => void;
  onJumpToStep: (step: lensConfigurationSteps) => Promise<void>;
}

const LensSummary = ({
  locale,
  translations,
  currencyCode,
  onJumpToStep,
  onCloseLensConfigurator,
}: ILensSummaryProps) => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const language = getLangFromLocale(locale);
  const {
    steps,
    loading,
    summary,
    currentStep,
    productInfo,
    skippedSteps,
    fromReviewStep,
    exitModalConfig,
    updateFromReviewStep,
  } = useConfiguratorContext();

  const total = useMemo(() => {
    return summary.reduce(
      (acc, item) =>
        acc + (item.price === "Included" ? 0 : parseFloat(item?.offerPrice || item?.price || "0")),
      0,
    );
  }, [summary]);

  const headerTitle =
    lensConfiguratorHeaderTitleMap?.[currentStep] ?? lensConfiguratorHeaderTitleMap.DEFAULT;

  const isLensTypeStep = currentStep === lensConfigurationSteps.LENS_TYPE;
  const isPrescriptionStep =
    currentStep === lensConfigurationSteps.PRESCRIPTION ||
    currentStep === lensConfigurationSteps.UPLOAD_PRESCRIPTION;

  const handleGoBack = async () => {
    if (!onJumpToStep || typeof onJumpToStep !== "function") return;
    startPageTransition(false);

    if (fromReviewStep && currentStep === lensConfigurationSteps.REVIEW) {
      updateFromReviewStep(false);
      await onJumpToStep(lensConfigurationSteps.REVIEW);
      endPageTransition();
      return;
    }

    switch (currentStep) {
      case lensConfigurationSteps.LENS_CUSTOMIZATION:
        await onJumpToStep(getPreviousStep(currentStep, steps, skippedSteps));
      case lensConfigurationSteps.PRESCRIPTION:
        await onJumpToStep(getPreviousStep(currentStep, steps));
      case lensConfigurationSteps.UPLOAD_PRESCRIPTION:
        await onJumpToStep(lensConfigurationSteps.PRESCRIPTION);
      case lensConfigurationSteps.REVIEW:
        await onJumpToStep(getPreviousStep(currentStep, steps, skippedSteps));
      case lensConfigurationSteps.ADDITIONAL_OPTIONS:
        await onJumpToStep(getPreviousStep(currentStep, steps, skippedSteps));
    }
    endPageTransition();
  };

  const handleCloseModal = () => {
    onCloseLensConfigurator?.();
  };

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((prev) => !prev);
  };
  const listPrice = productInfo?.price?.listPrice;
  return (
    <section
      //  className="col-span-full lg:col-span-4 border border-black box-border lg:w-[461px]"
      className="box-border w-full lg:w-[461px] "
    >
      {/* Header Title */}
      <section className="mt-4 lg:hidden max-sm:px-4">
        <div className="mb-5 flex items-center justify-between">
          <h1 className="text-sm font-semibold">
            {translations?.lensConfigurator?.lens_config_header_exit_prompt ??
              "Exit lens configurator"}
          </h1>
          <CloseIcon className="h-[14px] w-[14px]" onClick={handleToggleConfirmModal} />
        </div>
      </section>
      <hr className="-mx-4 sm:-mx-12 lg:hidden" />

      <Navigation
        disabled={loading}
        className="my-6 flex lg:hidden max-sm:px-4"
        onGoBack={isLensTypeStep ? handleToggleConfirmModal : handleGoBack}
      >
        {isLensTypeStep
          ? (translations?.lensConfigurator?.lens_config_header_exit_prompt ??
            "Exit lens configurator")
          : translations?.lensConfigurator?.[headerTitle]}
      </Navigation>

      {/* Lens Image Section */}
      <section className="mb-4 w-full bg-[#FAFAFA] px-6 md:px-9 py-7 md:py-12 lg:-mx-0">
        <LazyImage
          width={390}
          height={163}
          alt={productInfo?.name ?? ""}
          src={productInfo?.image?.url ?? noImage}
          className={(_, isError) =>
            cn(
              "w-[327] lg:w-[390px] rounded-lg h-[138px] lg:h-[163px]",
              !isError ? "invert-[2%]" : "max-h-[10.188rem] max-w-[24.375rem]",
            )
          }
          style={{ width: "390.85px", height: "163px" }}
        />
      </section>

      {/* Brand Section */}
      <section>
        <p className="mb-4 text-sm lg:hidden max-sm:px-4">{productInfo?.brand}</p>
        <hr className="col-span-full mb-4 flex lg:hidden" />
      </section>

      {/* Options Section */}
      {/* !isPrescriptionStep */}
      {hasArrayElements(summary) && (
        <section>
          {summary.map((item, index) => {
            const choice = ((item.choices ?? []).at(0) ?? {}) as ChoiceType;

            return (
              <section key={index} className="my-4">
                <SummaryView
                  className={cn(
                    item.key === "model-price" && "hidden",
                    item.key === lensConfigurationSteps.LENS_CUSTOMIZATION && "hidden",
                  )}
                  listPrice={
                    <RenderPrice price={listPrice} currencyCode={currencyCode} lang={language} />
                  }
                  name={translate(choice?.displayValue, language)}
                  title={translations?.lensConfigurator?.[item?.option]}
                  keys={item.key}
                  priceValue={item.key === "model-price" ? Number(item.price) : undefined}
                  listPriceValue={item.key === "model-price" ? listPrice : undefined}
                  price={
                    item.offerPrice ? (
                      <>
                        <p>
                          <div className="flex flex-col">
                            <del className="text-gray-500">
                              <RenderPrice
                                price={item.price}
                                currencyCode={currencyCode}
                                lang={language}
                              />
                            </del>
                            <span className={item.offerPrice !== item.price ? "text-red-500" : ""}>
                              <RenderPrice
                                price={item.offerPrice}
                                currencyCode={currencyCode}
                                lang={language}
                              />
                            </span>
                            {/* TODO(QA): When Qatar (qa) is enabled, gate offerPercent with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
                                via getPercentHideCountries()/displayPercentage (same as Magrabi LCP LHS summary). */}
                            <span className="text-gray-500">{item.offerPercent}</span>
                          </div>
                        </p>
                      </>
                    ) : item.price === "Included" ? (
                      "Included"
                    ) : (
                      <RenderPrice price={item.price} currencyCode={currencyCode} lang={language} />
                    )
                  }
                />
                <hr
                  className={cn(
                    "-mx4 -sm:mx-12 mt-4",
                    item.key === "model-price" && "hidden sm:block",
                    item.key === lensConfigurationSteps.LENS_CUSTOMIZATION && "hidden sm:block",
                  )}
                />
              </section>
            );
          })}
        </section>
      )}

      {/* Total Section */}
      <section className="mb-4">
        <TotalSection
          total={{
            label: translations?.lensConfigurator?.lens_config_total_label || "",
            amount: <RenderPrice price={total} currencyCode={currencyCode} lang={language} />,
          }}
        />
      </section>

      <ConfirmationModal
        open={openConfirmModal}
        onConfirm={handleCloseModal}
        onCancel={handleToggleConfirmModal}
        imgURL={exitModalConfig?.enableMedia ? exitModalConfig.media : ""}
        slots={{
          cancel:
            translations?.lensConfigurator
              ?.lens_config_exit_lens_configurator_overlay_no_button_label || "",
          confirm:
            translations?.lensConfigurator
              ?.lens_config_exit_lens_configurator_overlay_yes_button_label || "",
          title:
            translations?.lensConfigurator
              ?.lens_config_exit_lens_configurator_overlay_description || "",
        }}
      />
    </section>
  );
};

export default LensSummary;
